// "See all notifications" from the bell (owner, 2026-10-25): a compact popup instead of a page.
// Tabs All / Unread / From Adyvance, type chips (orders, payments, stock, plan and wallet, other),
// grouped by day, "Show older". A notification with a destination opens it (and is marked read);
// one without expands in place with its details and the page it belongs to. Messages from Adyvance
// expand to their full text.
import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatDateTime, formatMoney, formatRelative } from "@ady/format";
import { t } from "@ady/i18n";
import { Button, cx, ErrorState, Icon, Popup, SkeletonText, type IconName } from "@ady/ui";
import { AppLink, useGo } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { NoticeLink, useNotices } from "../shell/ControlCentre";
import { notificationsApi, notifyKeys, type AppNotification } from "./api";
import { POLL_MS, resolveLink, titleOf, useLiveNotifications } from "./live";

type Tab = "all" | "unread" | "adyvance";
type Kind = "orders" | "payments" | "stock" | "billing" | "other";
const KINDS: Kind[] = ["orders", "payments", "stock", "billing", "other"];

const KIND_OF: Record<string, Kind> = {
  order: "orders",
  shipment: "orders",
  return: "orders",
  payment: "payments",
  inventory: "stock",
  billing: "billing",
};
const kindOf = (n: AppNotification): Kind => KIND_OF[n.event.split(".")[0] ?? ""] ?? "other";
const ICON: Record<Kind, IconName> = {
  orders: "orders",
  payments: "money",
  stock: "box",
  billing: "wallet",
  other: "bell",
};
/** Where a notification without its own link belongs (admin path below /{org}/{store}/). */
const HOME: Partial<Record<Kind, "orders" | "inventory" | "wallet">> = {
  orders: "orders",
  payments: "orders",
  stock: "inventory",
  billing: "wallet",
};

const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return t("notifications.all.today");
  if (d.toDateString() === yesterday.toDateString()) return t("notifications.all.yesterday");
  return formatDate(iso);
};

export function AllNotificationsPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Popup
      open={open}
      onClose={onClose}
      label={t("notifications.centre.title")}
      size="sm"
      data-testid="all-notifications"
    >
      {open ? <AllNotifications onClose={onClose} /> : null}
    </Popup>
  );
}

function AllNotifications({ onClose }: { onClose: () => void }) {
  const { me, membership, orgSlug, storeSlug } = useShell();
  const orgId = membership.org.id;
  const [tab, setTab] = useState<Tab>("all");
  const [kind, setKind] = useState<Kind | "all">("all");
  const live = useLiveNotifications(orgId);
  const poll = live === "polling" ? POLL_MS : 0;
  const qc = useQueryClient();
  const unread = tab === "unread";
  const q = useInfiniteQuery({
    queryKey: [...notifyKeys.list(orgId, unread), "pages"],
    queryFn: ({ pageParam }) => notificationsApi.list(orgId, { unread, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: poll || false,
    enabled: tab !== "adyvance",
  });
  const notices = useNotices(orgId, !me.support);
  const refresh = () => qc.invalidateQueries({ queryKey: notifyKeys.all(orgId) });
  const setRead = useMutation({
    mutationFn: (v: { n: AppNotification; read: boolean }) =>
      notificationsApi.setRead(orgId, v.n.id, v.read),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => notificationsApi.readAll(orgId),
    onSuccess: refresh,
  });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  const counts = useMemo(() => {
    const c: Record<Kind, number> = { orders: 0, payments: 0, stock: 0, billing: 0, other: 0 };
    for (const n of items) c[kindOf(n)]++;
    return c;
  }, [items]);
  const shown = kind === "all" ? items : items.filter((n) => kindOf(n) === kind);
  const unreadCount = items.filter((n) => !n.read).length;
  const base = `/${orgSlug}/${storeSlug}`;

  const tabs: [Tab, string, number | null][] = [
    ["all", t("notifications.centre.all"), null],
    ["unread", t("notifications.centre.unread"), tab === "all" ? unreadCount || null : null],
    [
      "adyvance",
      t("plans.shop.notices.title"),
      (notices.data?.data ?? []).filter((n) => !n.seen).length || null,
    ],
  ];

  return (
    <div className="flex flex-col gap-2.5">
      <header className="flex items-center gap-2 border-b border-subtle pb-2.5">
        <span className="flex size-7 flex-none items-center justify-center rounded-xs ady-bg-brand text-on-primary">
          <Icon name="bell" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-base font-semibold">{t("notifications.centre.title")}</h2>
          <span className="inline-flex items-center gap-1 text-2xs text-fg-tertiary">
            <span
              className={cx(
                "size-1.5 rounded-full",
                live === "live" ? "bg-success-fg" : "bg-fg-tertiary",
              )}
              aria-hidden
            />
            {live === "live"
              ? t("notifications.centre.live")
              : t("notifications.centre.pollingShort")}{" "}
            · {t("notifications.all.kept")}
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
        >
          <Icon name="x" />
          <span className="ady-sr-only">{t("notifications.all.close")}</span>
        </button>
      </header>

      <div className="flex items-center justify-between gap-2">
        <span
          role="tablist"
          aria-label={t("notifications.centre.title")}
          className="inline-flex rounded-xs bg-sunken p-0.5"
        >
          {tabs.map(([id, label, n]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => {
                setTab(id);
                setKind("all");
              }}
              className={cx(
                "inline-flex items-center gap-1 rounded-xs px-2 py-1 text-xs font-medium",
                tab === id ? "bg-surface text-fg shadow-1" : "text-fg-secondary hover:text-fg",
              )}
            >
              {label}
              {n ? (
                <span className="rounded-full bg-danger-bg px-1 text-2xs font-semibold text-danger-fg">
                  {n}
                </span>
              ) : null}
            </button>
          ))}
        </span>
        {tab !== "adyvance" && unreadCount > 0 ? (
          <button
            type="button"
            className="text-2xs font-semibold text-link disabled:opacity-60"
            disabled={readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            {t("notifications.centre.markAllRead")}
          </button>
        ) : null}
      </div>

      {tab === "adyvance" ? (
        <NoticeList />
      ) : (
        <>
          {items.length ? (
            <span className="flex flex-wrap gap-1">
              <Chip on={kind === "all"} onClick={() => setKind("all")}>
                {t("notifications.all.types.all")}
              </Chip>
              {KINDS.filter((k) => counts[k] > 0).map((k) => (
                <Chip
                  key={k}
                  on={kind === k}
                  onClick={() => setKind(k)}
                  icon={ICON[k]}
                  count={counts[k]}
                >
                  {t(`notifications.all.types.${k}`)}
                </Chip>
              ))}
            </span>
          ) : null}
          {q.isPending ? (
            <SkeletonText lines={4} />
          ) : q.isError ? (
            <ErrorState size="inline" body={t("errors.generic")} onRetry={() => void q.refetch()} />
          ) : shown.length === 0 ? (
            <span className="flex items-center gap-2 rounded-sm border border-dashed border-control px-3 py-4 text-xs text-fg-tertiary">
              <Icon name="checkCircle" />
              {unread
                ? t("notifications.centre.emptyUnread")
                : items.length
                  ? t("notifications.all.noneType")
                  : t("notifications.centre.empty")}
            </span>
          ) : (
            <ul className="flex flex-col" data-testid="all-notifications-list">
              {shown.map((n, i) => {
                const day = dayLabel(n.created_at);
                const first = i === 0 || dayLabel(shown[i - 1]!.created_at) !== day;
                return (
                  <li key={n.id} className="flex flex-col">
                    {first ? (
                      <span className="px-2 pt-1.5 pb-0.5 text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
                        {day}
                      </span>
                    ) : null}
                    <Row
                      n={n}
                      base={base}
                      onGo={() => {
                        if (!n.read) setRead.mutate({ n, read: true });
                        onClose();
                      }}
                      onExpand={() => {
                        if (!n.read) setRead.mutate({ n, read: true });
                      }}
                      onUnread={() => setRead.mutate({ n, read: false })}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          {q.hasNextPage ? (
            <Button
              size="sm"
              variant="secondary"
              loading={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              {t("notifications.centre.loadMore")}
            </Button>
          ) : null}
        </>
      )}

      <footer className="flex items-center justify-between gap-2 border-t border-subtle pt-2">
        <AppLink
          href={`${base}/settings/notifications`}
          onClick={onClose}
          className="inline-flex items-center gap-1 text-xs font-medium text-link no-underline hover:underline"
        >
          <Icon name="gear" />
          {t("notifications.all.settings")}
        </AppLink>
        <AppLink
          href={`${base}/notifications`}
          onClick={onClose}
          className="text-xs font-medium text-fg-secondary no-underline hover:underline"
        >
          {t("notifications.all.page")}
        </AppLink>
      </footer>
    </div>
  );
}

function Chip({
  on,
  onClick,
  children,
  icon,
  count,
}: {
  on: boolean;
  onClick: () => void;
  children: string;
  icon?: IconName;
  count?: number;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-semibold",
        on
          ? "border-link bg-selected text-link"
          : "border-control text-fg-secondary hover:bg-hover",
      )}
    >
      {icon ? <Icon name={icon} className="size-3" /> : null}
      {children}
      {count !== undefined ? <span className="tabular-nums">· {count}</span> : null}
    </button>
  );
}

/** One notification: go where it belongs, or expand in place when it has no page of its own. */
function Row({
  n,
  base,
  onGo,
  onExpand,
  onUnread,
}: {
  n: AppNotification;
  base: string;
  onGo: () => void;
  onExpand: () => void;
  onUnread: () => void;
}) {
  const { membership } = useShell();
  const go = useGo();
  const [open, setOpen] = useState(false);
  const k = kindOf(n);
  const href = resolveLink(n.link, membership);
  const amount =
    typeof n.params.total_paisa === "number" ? formatMoney(n.params.total_paisa) : null;
  const home = HOME[k];
  const line = (
    <>
      <span
        className={cx("size-1.5 flex-none rounded-full", n.read ? "bg-transparent" : "bg-link")}
        aria-hidden
      />
      <span
        className={cx(
          "flex size-6 flex-none items-center justify-center rounded-full",
          n.read ? "bg-sunken" : "ady-bg-brand-tint",
        )}
      >
        <Icon name={ICON[k]} gradient={!n.read} className="size-3.5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cx("truncate text-xs", n.read ? "text-fg-secondary" : "font-semibold text-fg")}
        >
          {titleOf(n)}
        </span>
        {amount ? <span className="text-2xs text-fg-tertiary">{amount}</span> : null}
      </span>
      <span className="flex-none text-2xs text-fg-tertiary">{formatRelative(n.created_at)}</span>
      <Icon
        name={href ? "chevronRight" : open ? "chevronUp" : "chevronDown"}
        className="size-3.5 flex-none text-fg-tertiary"
      />
    </>
  );
  const cls =
    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-start text-fg no-underline hover:bg-hover";
  return (
    <div
      className={cx("flex flex-col rounded-sm", open && "bg-sunken")}
      data-testid="all-notifications-row"
      data-read={n.read ? "true" : "false"}
    >
      {href ? (
        <AppLink href={href} onClick={onGo} className={cls}>
          {line}
        </AppLink>
      ) : (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => {
            setOpen((v) => !v);
            onExpand();
          }}
          className={cls}
        >
          {line}
        </button>
      )}
      {open && !href ? (
        <div className="flex flex-col gap-1.5 px-2 pb-2 ps-[3.25rem]">
          {titleOf(n).length > 44 ? (
            <span className="text-xs whitespace-normal">{titleOf(n)}</span>
          ) : null}
          <span className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-2xs">
            <span className="text-fg-tertiary">{t("notifications.all.when")}</span>
            <span>{formatDateTime(n.created_at)}</span>
            {amount ? (
              <>
                <span className="text-fg-tertiary">{t("notifications.all.amount")}</span>
                <span>{amount}</span>
              </>
            ) : null}
            <span className="text-fg-tertiary">{t("notifications.all.type")}</span>
            <span>{t(`notifications.all.types.${k}`)}</span>
          </span>
          <span className="flex items-center gap-2">
            {home ? (
              <Button
                size="sm"
                variant="secondary"
                icon="chevronRight"
                onClick={() => {
                  onGo();
                  go(`${base}/${home}`);
                }}
              >
                {t(`notifications.all.goTo.${home}`)}
              </Button>
            ) : null}
            <button type="button" className="text-2xs font-semibold text-link" onClick={onUnread}>
              {t("notifications.all.markUnread")}
            </button>
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Messages from Adyvance: the title and date; a click shows the whole message and its link. */
function NoticeList() {
  const { me, membership } = useShell();
  const q = useNotices(membership.org.id, !me.support);
  const [open, setOpen] = useState<string | null>(null);
  const list = q.data?.data ?? [];
  if (q.isPending) return <SkeletonText lines={3} />;
  if (!list.length)
    return (
      <span className="flex items-center gap-2 rounded-sm border border-dashed border-control px-3 py-4 text-xs text-fg-tertiary">
        <Icon name="checkCircle" />
        {t("notifications.all.noNotices")}
      </span>
    );
  return (
    <ul className="flex flex-col" data-testid="all-notifications-notices">
      {list.map((n) => {
        const expanded = open === n.id;
        return (
          <li key={n.id} className={cx("flex flex-col rounded-sm", expanded && "bg-sunken")}>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setOpen(expanded ? null : n.id)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-start hover:bg-hover"
            >
              <span className="flex size-6 flex-none items-center justify-center rounded-full ady-bg-brand-tint">
                <Icon name="megaphone" gradient className="size-3.5" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cx(
                    "text-xs",
                    !expanded && "truncate",
                    n.seen ? "text-fg-secondary" : "font-semibold",
                  )}
                >
                  {n.title}
                </span>
                {!expanded ? (
                  <span className="truncate text-2xs text-fg-tertiary">{n.body}</span>
                ) : null}
              </span>
              <span className="flex-none text-2xs text-fg-tertiary">
                {n.created_at ? formatDate(n.created_at) : ""}
              </span>
              <Icon
                name={expanded ? "chevronUp" : "chevronDown"}
                className="size-3.5 flex-none text-fg-tertiary"
              />
            </button>
            {expanded ? (
              <div className="flex flex-col gap-1.5 px-2 pb-2 ps-10">
                <span className="text-xs whitespace-pre-line text-fg-secondary">{n.body}</span>
                {n.link ? (
                  <NoticeLink link={n.link} className="w-fit text-xs font-semibold text-link" />
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
